export const FTM_2024_ARTICLES = [
  {
    slug: 'install-family-tree-maker-2024-windows',
    title: 'Your Windows PC and Family Tree Maker 2024: A Setup Walkthrough',
    excerpt:
      'A plain-English guide to downloading the program, installing it, and entering your serial number on a PC running Windows 10 or 11, plus fixes for a stubborn installer.',
    intro: [
      'Getting Family Tree Maker 2024 onto a Windows computer is usually a one-sitting job. Most people are finished in well under half an hour, and a good share of that time is simply waiting for the download to complete.',
      'Before you begin, find the email or receipt that holds your serial number, and make sure the Windows account you are using has administrator rights. On a shared family computer, the person who first set the machine up usually has that kind of account.',
    ],
    sections: [
      {
        heading: 'A little groundwork first',
        bullets: [
          'Compare your PC with the published system requirements, paying special attention to free disk space if you keep lots of photos.',
          'If you already use an older Family Tree Maker edition, make a fresh backup of each tree and copy it to a flash drive or cloud folder.',
          'Shut down programs you do not need right now, such as web browsers with many tabs or photo editors.',
          'Plug in your laptop so it does not go to sleep partway through.',
        ],
      },
      {
        heading: 'Downloading and running the setup file',
        steps: [
          'Open your purchase email and click the download link, or sign in to the account where you bought the program and download it from there.',
          'When the download finishes, open your Downloads folder and double-click the Family Tree Maker setup file.',
          'Windows will pop up a box asking whether this app may make changes to your device. Click Yes.',
          'Read through the license screen, agree to it, and let the setup program use the folder it suggests unless you have a good reason to pick another.',
          'Wait while the files are copied. A progress bar shows how far along things are.',
          'Click Finish, then start Family Tree Maker 2024 from the Start menu or the new desktop shortcut.',
          'Type or paste your serial number when prompted, stay online until activation succeeds, and then open a tree you already have or start a brand new one.',
        ],
      },
      {
        heading: 'Why a Windows installation sometimes stalls',
        paragraphs: [
          'Most setup hiccups trace back to one of three places. The download may have been cut short, leaving a file that looks normal but is incomplete. Security software may quietly block the setup program because it is writing files into protected folders. Or Windows itself may be halfway through its own updates and waiting for a restart before it lets new software in.',
        ],
      },
      {
        heading: 'Getting a stuck installation moving again',
        bullets: [
          'Right-click the setup file, then click Run as administrator.',
          'Delete the setup file, download a brand new copy, and try again.',
          'Open Windows Update, install anything that is waiting, and restart before your next attempt.',
          'Switch off real-time scanning in your antivirus just long enough to install, then turn it straight back on.',
          'Check that the drive has enough free room. A nearly full drive can make setup fail without a clear message.',
        ],
      },
      {
        heading: 'Mistakes that often trip people up',
        bullets: [
          'Running the installer from inside the email or browser window instead of saving it to the computer first.',
          'Typing the serial number by hand and mixing up letters and numbers that look alike.',
          'Installing while signed in to a limited Windows account, which can lead to permission errors later on.',
          'Uninstalling the older edition before backing up the trees it holds.',
        ],
      },
      {
        heading: 'Quick checklist when you are finished',
        bullets: [
          'The program opens with no error messages.',
          'The program shows as activated instead of asking for a serial number again.',
          'Your existing tree opens, and the number of people looks right.',
          'Pictures in the Media workspace display properly rather than showing as broken links.',
          'You have used the Help menu to check for updates and installed anything offered.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is my older edition removed when I install 2024?',
        a: 'Normally not. The new edition installs as a separate program, so the older one stays put until you decide to uninstall it. Your existing tree files are left alone, and when you open one in 2024 the program converts it to the newer format.',
      },
      {
        q: 'Do I need to be online to install?',
        a: 'You need an internet connection to download the installer and to activate the program. After that, most everyday work in your tree can be done offline, although FamilySync, Web Search, and updates all need a connection.',
      },
      {
        q: 'Can I put it on a second computer?',
        a: 'Your license terms decide how many computers you may activate, so check the details that came with your purchase. If you are replacing an old computer, move your trees across with a backup file rather than copying loose folders.',
      },
      {
        q: 'Where do my trees get saved after installing?',
        a: 'By default, Family Tree Maker keeps trees in a Family Tree Maker folder within your Documents folder. You are free to save a tree somewhere else if you prefer, as long as you remember where it is.',
      },
    ],
  },
  {
    slug: 'install-family-tree-maker-2024-mac',
    title: 'Getting Family Tree Maker 2024 Up and Running on macOS',
    excerpt:
      'Step-by-step help for installing the 2024 edition on your Mac, clearing Apple security warnings, and bringing trees across from an earlier edition.',
    intro: [
      'Installing the 2024 edition of Family Tree Maker on a Mac follows the same pattern as most Mac software. You download a file, place the program in your Applications folder, and then unlock it with your serial number.',
      'Apple builds several layers of protection into macOS, so you may see a warning or two the first time the program starts. These messages are routine and do not mean anything is wrong with your Mac or with the download.',
    ],
    sections: [
      {
        heading: 'Before you start on a Mac',
        bullets: [
          'Find out which version of macOS you have by opening the Apple menu and choosing About This Mac, then compare it with the published requirements.',
          'Keep your serial number close by, either in your purchase email or on your receipt.',
          'Know your Mac login password, since macOS may ask for it while copying the program.',
          'If you use an older edition, back up your tree before doing anything else.',
        ],
      },
      {
        heading: 'Putting the program on your Mac',
        steps: [
          'Download the Mac version through the link in your purchase email or from your online account.',
          'In your Downloads folder, double-click the file you just saved to open it.',
          'Follow the on-screen instructions, which usually involve dragging the Family Tree Maker icon into your Applications folder.',
          'Open Applications in Finder and double-click Family Tree Maker 2024.',
          'If macOS asks whether you are sure you want to open an app downloaded from the internet, click Open.',
          'Enter your serial number and wait for the confirmation that activation worked.',
        ],
      },
      {
        heading: 'Handling Apple security warnings',
        paragraphs: [
          'Now and then macOS refuses to open a newly downloaded program and says it cannot be checked or verified. When that happens, open System Settings (called System Preferences on older versions of macOS), go to Privacy & Security, and look near the bottom for a note about Family Tree Maker with an Open Anyway button.',
          'The program may also request permission to use folders such as Documents, Desktop, or Photos. Saying yes lets it save your tree where you expect and link pictures from your photo library. If you clicked Don\'t Allow by accident, you can change your answer later in that same Privacy & Security area.',
        ],
      },
      {
        heading: 'Carrying a tree over from an earlier Mac edition',
        steps: [
          'Open the tree in your old edition and use Backup in the File menu, making sure media is included.',
          'Launch the new edition and use Restore in the File menu to open that backup.',
          'Give the restored tree a clear new name so you can tell it apart from the old copy.',
          'Visit the Media workspace and look for any pictures marked as missing. If you spot some, run Find Missing Media.',
        ],
      },
      {
        heading: 'How the Mac version differs from Windows',
        bullets: [
          'Menus sit in the bar at the very top of the screen on a Mac rather than inside the program window.',
          'Keyboard shortcuts use the Command key where Windows uses the Control key.',
          'Security approvals happen in System Settings on a Mac, while Windows uses its own pop-up permission box.',
          'Backup files can generally be restored on either kind of computer, which is handy if you ever switch from one to the other.',
        ],
      },
      {
        heading: 'If the program still will not open',
        bullets: [
          'Make sure you are starting the copy in Applications, not the one inside the downloaded file window.',
          'Restart the Mac, since a fresh start clears many first-launch glitches.',
          'Install any waiting macOS updates from System Settings.',
          'Drag the program to the Trash, download a new copy, and install it again.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Will it work on a Mac with an Apple chip?',
        a: 'Current editions are made for today\'s Macs, including models with Apple chips. The publisher\'s requirements page lists which versions of macOS are supported, so look it over before buying if your Mac is on the older side.',
      },
      {
        q: 'Why does my Mac say the app cannot be verified?',
        a: 'This is a general macOS safety message that appears for many programs downloaded from the internet. As long as you downloaded from your official purchase link, you can approve it in Privacy & Security.',
      },
      {
        q: 'Can my old edition stay on the Mac as well?',
        a: 'In most cases the two can sit side by side in Applications. Just take care to open the tree you mean to work on, and do all new work in one edition once you have moved over.',
      },
      {
        q: 'My photos disappeared after moving the tree. What happened?',
        a: 'Usually the pictures were left out of the backup, or they live in a folder the new copy cannot find. Restore from a backup made with media switched on, or run Find Missing Media and point it to the right folder.',
      },
    ],
  },
  {
    slug: 'system-requirements-ftm-2024',
    title: 'Will My Computer Run Family Tree Maker 2024? A Hardware Guide',
    excerpt:
      'What kind of Windows PC or Mac the 2024 edition needs, how to check your own machine in a few clicks, and what keeps big trees running smoothly.',
    intro: [
      'Most desktop and laptop computers bought in the last several years can handle Family Tree Maker 2024 without much fuss. The software does not need a high-end machine, but it does appreciate enough memory and some breathing room on the hard drive.',
      'The official requirements come from the publisher and may be revised as updates are released, so treat the figures below as a general guide and confirm the current list before you buy. The rest of this page shows you how to check your own computer and explains what matters most as your tree grows.',
    ],
    sections: [
      {
        heading: 'What a Windows PC generally needs',
        bullets: [
          'Windows 10 or Windows 11, kept up to date',
          '4 GB of memory as a bare minimum, though 8 GB or more makes a noticeable difference',
          'Several gigabytes of free storage for the program, plus extra space for photos, documents, and backups',
          'A screen of at least 1280 by 800 pixels so the workspaces fit comfortably',
          'Internet access for activating, downloading updates, syncing with Ancestry, and searching online records',
        ],
      },
      {
        heading: 'What a Mac generally needs',
        bullets: [
          'A current or recent release of macOS',
          'Either an Intel processor or an Apple chip',
          'The same memory guidance as on Windows: 4 GB to get by, 8 GB or more to be comfortable',
          'Enough free storage for the program, your media files, and backup copies',
          'A working internet connection so you can activate, update, use FamilySync, and run Web Search',
        ],
      },
      {
        heading: 'How to look up your own computer\'s details',
        paragraphs: [
          'You do not need to open the case or know any technical terms. Both Windows and macOS will tell you what you have in a couple of clicks.',
        ],
        steps: [
          'On Windows, open Settings, choose System, and then About. Your Windows edition and installed RAM are listed there.',
          'To see free space on Windows, open File Explorer, click This PC, and look at the bar under your main drive.',
          'On a Mac, click the Apple menu and choose About This Mac to see your macOS version, chip, and memory.',
          'For free space on a Mac, open System Settings, choose General, and then Storage.',
        ],
      },
      {
        heading: 'What really makes a difference to speed',
        bullets: [
          'Memory matters most. More RAM lets the program keep more of your tree ready to use at once.',
          'A solid-state drive (SSD) opens trees and loads pictures far faster than an older spinning hard drive.',
          'Keeping the tree on the computer\'s own drive, rather than a network drive or a folder that constantly syncs to the cloud, avoids slowdowns and file conflicts.',
          'A tidy tree runs better. Running Compact File from time to time shrinks the database and can speed things up.',
        ],
      },
      {
        heading: 'Planning ahead for a very large tree',
        paragraphs: [
          'If your tree holds several tens of thousands of individuals, or thousands of photos and scanned records, aim well above the minimum. A computer with an SSD and around 16 GB of memory will feel much more responsive, especially when you build charts, print reports, or sync with Ancestry.',
          'It also pays to keep your media folder organized and to store backups on a separate drive, so the main drive does not slowly fill up over the years.',
        ],
      },
      {
        heading: 'Devices that cannot run the program',
        bullets: [
          'Chromebooks, because they cannot install Windows or Mac desktop software',
          'iPads, iPhones, and Android phones or tablets',
          'Very old computers running operating systems that no longer receive security updates',
        ],
      },
    ],
    faqs: [
      {
        q: 'Will the program run on a Chromebook or a tablet?',
        a: 'No. It is a desktop program for Windows and Mac only. If you want to view your family on a phone or tablet, one option is to sync the tree to Ancestry and use the Ancestry app.',
      },
      {
        q: 'Is 4 GB of memory really enough?',
        a: 'The program will usually run, but you may notice sluggish moments with bigger trees or lots of pictures. If you are shopping for a new computer, 8 GB or more is the safer choice.',
      },
      {
        q: 'Must I be connected to the internet the whole time?',
        a: 'No. You can add and edit information offline. A connection is only needed to activate, install updates, use FamilySync or FamilySearch features, and run Web Search.',
      },
      {
        q: 'Can my photos live on an external drive?',
        a: 'Yes, but that drive has to be plugged in whenever you work with those pictures, and its drive letter or name should stay the same. Otherwise Family Tree Maker may report the media as missing.',
      },
    ],
  },
  {
    slug: 'upgrade-to-family-tree-maker-2024',
    title: 'Moving Up to Family Tree Maker 2024 With Your Research Intact',
    excerpt:
      'A careful, step-by-step plan for leaving the 2019, 2017, or 2014 edition behind so your people, pictures, and Ancestry link all make the trip safely.',
    intro: [
      'An upgrade to the 2024 edition is mostly about protecting what you already have. Your research may represent years of effort, so the aim is to carry it into the new edition with nothing lost on the way.',
      'The good news is that the process is dependable when you do things in the right order. Update the old edition, make a solid backup, install the new one, and then check the results before you retire the old copy.',
    ],
    sections: [
      {
        heading: 'Why the order of steps matters',
        paragraphs: [
          'The first time the 2024 edition opens one of your trees, the program converts it to the newer file format. That conversion only goes one way. If something odd turns up later, the backup you made beforehand is your only route back, which is why every step below builds toward that safety copy.',
        ],
      },
      {
        heading: 'The upgrade from start to finish',
        steps: [
          'In your current edition, use the Help menu to check for updates and install the newest build it offers.',
          'If your tree is connected to Ancestry, run a sync and let it finish completely before moving on.',
          'Use Backup in the File menu, ticking the option to include media files.',
          'Copy that backup to a second place, such as a flash drive or a cloud folder.',
          'Run the 2024 installer, then type in your new serial number when asked.',
          'Open your tree in the new edition and let the conversion run. Large trees can take a while, so be patient.',
          'Compare the number of people, look at a few favorite photos, and check the sync status in the Plan workspace.',
        ],
      },
      {
        heading: 'Bringing forward a tree from a far older release',
        paragraphs: [
          'Trees created in far older releases can be harder to bring forward in one jump. If the new edition will not open the file directly, try restoring a backup copy of it; that sometimes succeeds where opening the original does not.',
          'If that still fails, export a GEDCOM file from the old program and import it into Family Tree Maker 2024. A GEDCOM carries people, relationships, events, and sources, but you should check that media links and some formatting came through, as those do not always transfer perfectly.',
        ],
      },
      {
        heading: 'Keeping your Ancestry connection intact',
        paragraphs: [
          'If your tree was linked to an Ancestry online tree, that link generally carries into the converted file. After upgrading, open the Plan workspace, look at the sync status, and run a sync. If the program reports a problem, resist the urge to create a brand new online tree right away. First work out which copy holds your latest changes, so you do not end up with two competing versions.',
        ],
      },
      {
        heading: 'Upgrade mistakes worth avoiding',
        bullets: [
          'Skipping the backup because the upgrade "should be fine."',
          'Leaving a sync unfinished in the old edition, which can cause recent changes to go missing.',
          'Continuing to edit the old tree after conversion, so your work ends up split between two files.',
          'Deleting the old edition and its files before confirming the new tree is complete.',
        ],
      },
      {
        heading: 'Tidying up afterward',
        bullets: [
          'Hold on to your pre-upgrade backup for at least several weeks.',
          'Reconnect any add-on programs you use, for example Family Book Creator.',
          'Run Compact File once to tidy the freshly converted tree.',
          'Start a new backup habit in 2024, with the date in each file name.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can I go back to opening the tree in my old edition?',
        a: 'Generally no. The converted file is saved in a format that older editions cannot read. Your pre-upgrade backup is the copy to use if you ever need to return to the old program.',
      },
      {
        q: 'Will my old serial number work for 2024?',
        a: 'Usually not. Each edition has its own serial number, so you will need the one that came with your 2024 purchase.',
      },
      {
        q: 'How long does the conversion take?',
        a: 'Small trees convert in a minute or two. Trees with many thousands of people and lots of media can take noticeably longer. Let it finish without closing the program or putting the computer to sleep.',
      },
      {
        q: 'Do my sources and notes come across?',
        a: 'Yes. Opening or restoring the tree in 2024 brings over people, events, sources, citations, notes, and media links. Spot-check a few entries afterward to be sure everything looks right.',
      },
    ],
  },
  {
    slug: 'ftm-2024-vs-previous-versions',
    title: 'Family Tree Maker 2024 Next to 2019 and 2017: What Is New and What Is Not',
    excerpt:
      'A practical look at what feels familiar and what has improved in the 2024 edition, to help you decide whether upgrading makes sense right now.',
    intro: [
      'If you already use Family Tree Maker 2019 or 2017, opening the 2024 edition feels a bit like walking into a familiar house that has been freshened up. The layout, the workspaces, and the way trees are stored all follow the same overall design.',
      'That familiarity is a real plus for anyone who does not want to learn new software from scratch. The question most people have is not how hard the switch will be, but whether it is worth making yet. This page is meant to help you answer that.',
    ],
    sections: [
      {
        heading: 'Familiar features you will recognize',
        bullets: [
          'The row of workspaces across the top: Plan, People, Places, Media, Sources, Publish, and Web Search.',
          'Tree files kept on your own computer, with backups made from the File menu.',
          'FamilySync for keeping your tree matched with an Ancestry online tree.',
          'FamilySearch integration for comparing and sharing family information.',
          'A wide choice of charts, reports, and book-style printing options in the Publish workspace.',
        ],
      },
      {
        heading: 'Where the newer edition helps',
        paragraphs: [
          'Rather than a total redesign, each new edition tends to bring steady improvements. For most users, the benefits fall into a handful of areas.',
        ],
        bullets: [
          'Support for current versions of Windows and macOS, which older editions may stop receiving.',
          'Continued bug fixes released by the publisher.',
          'Refinements to speed, media handling, and connections with online services.',
          'For the exact list of changes, the publisher\'s release notes are the most reliable source.',
        ],
      },
      {
        heading: 'Signs it is time to upgrade',
        bullets: [
          'You are buying a new computer or moving to a newer operating system.',
          'Your current edition no longer gets updates, or it shows errors when syncing.',
          'You rely on FamilySync and want the best possible compatibility with Ancestry.',
          'You want fixes or features that are listed only for the newer release.',
        ],
      },
      {
        heading: 'When staying put is perfectly fine',
        paragraphs: [
          'If your present edition opens reliably, is fully updated, syncs without complaint, and you have no plans to change computers, there is no pressure to switch today. Just keep making regular backups so you are ready whenever you do decide to move.',
        ],
      },
      {
        heading: 'Settling in after you switch',
        steps: [
          'Open your converted tree and click through each workspace once to see where everything lives.',
          'Build a chart or report you use often in Publish and compare it with the old version.',
          'If you use FamilySync, run a sync to confirm the Ancestry link still works.',
          'Make your first backup in the new edition.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Will my saved charts and reports look exactly the same?',
        a: 'They come across with your tree, but a few templates may be laid out a little differently because of design updates. Preview any chart you plan to print before sending it to the printer.',
      },
      {
        q: 'Will I have to relearn the program?',
        a: 'Very little. Anyone comfortable with 2017 or 2019 will find the main screens and menus in familiar places.',
      },
      {
        q: 'Can 2019 and 2024 both be installed on one computer?',
        a: 'In most cases, yes, but settle on one edition for ongoing work. Editing the same tree in both quickly leads to confusion about which file is current.',
      },
      {
        q: 'Will my older edition keep syncing with Ancestry?',
        a: 'That depends on whether it is still supported. Older editions can stop syncing as online services change over time, which is one of the most common reasons people decide to upgrade.',
      },
    ],
  },
  {
    slug: 'activate-family-tree-maker-2024',
    title: 'Serial Number and Activation Trouble in Family Tree Maker 2024',
    excerpt:
      'What to check when Family Tree Maker 2024 rejects your serial number, calls the key invalid, or says you have run out of activations.',
    intro: [
      'Activation is the step where Family Tree Maker 2024 confirms that your copy was purchased properly. It normally happens once, right after installation, and takes only a moment when everything lines up.',
      'When it does not go smoothly, the cause is almost always simple: a mistyped character, a serial number meant for a different edition, a security program blocking the connection, or a license that has already been used on too many computers. Each of these has a straightforward fix.',
    ],
    sections: [
      {
        heading: 'Activating for the first time',
        steps: [
          'As soon as installation wraps up, launch Family Tree Maker 2024.',
          'Locate your serial number in your purchase email or on the product packaging.',
          'Copy the number and paste it into the activation box so every character is exact.',
          'Keep your computer online and wait for the confirmation message.',
          'Close and reopen the program to make sure it no longer asks for the number.',
        ],
      },
      {
        heading: 'Why activation can fail',
        paragraphs: [
          'Family Tree Maker has to reach an activation server to check your serial number. If that check cannot be completed, or the number does not match what the server expects, activation will not go through. The error message may sound alarming, but it very rarely points to a problem with your purchase itself.',
        ],
      },
      {
        heading: 'Fixes to try, in this order',
        bullets: [
          'Paste the serial number instead of typing it, which avoids confusing a zero with the letter O or a one with the letter I.',
          'Remove any stray spaces before or after the number.',
          'Double-check that the number came with a 2024 purchase, not an earlier release or another product.',
          'Make sure your internet works by opening any web page.',
          'Add Family Tree Maker to the allowed list in your firewall or antivirus, or pause that software briefly.',
          'Restart the computer and give it one more try.',
        ],
      },
      {
        heading: 'Running out of activations',
        paragraphs: [
          'Each license allows a limited number of activations. If you have replaced computers or reinstalled several times, you can hit that ceiling even though you only use one machine. When that happens, get in touch with the store you bought from or the publisher\'s customer service and ask them to reset your activations.',
        ],
      },
      {
        heading: 'Keeping your serial number safe',
        bullets: [
          'File the purchase email in a folder you will remember, or print it and keep it with your genealogy papers.',
          'Write the number down and store it somewhere secure, away from the computer itself.',
          'Save a copy in a password manager if you use one.',
        ],
      },
    ],
    faqs: [
      {
        q: 'I cannot find my serial number. What now?',
        a: 'Search your email for words such as Family Tree Maker, order, or receipt. If the message is gone, the retailer or publisher you purchased from can usually track down the order using your name and email address.',
      },
      {
        q: 'Does activation require the internet?',
        a: 'Yes. The program must connect to confirm your serial number. Once activation is done, you can work in your tree offline.',
      },
      {
        q: 'Does reinstalling use up another activation?',
        a: 'It can. If you are switching machines, try to finish the move in one go rather than installing and removing the program many times.',
      },
      {
        q: 'Can I use the serial number from my 2019 copy?',
        a: 'No. Serial numbers are tied to a specific edition, so Family Tree Maker 2024 needs its own.',
      },
    ],
  },
  {
    slug: 'backup-and-restore-ftm-2024',
    title: 'Protecting Your Research: Backups and Restores in FTM 2024',
    excerpt:
      'How to create a complete Family Tree Maker 2024 backup, where to keep it, and how to restore it safely on the same computer or a new one.',
    intro: [
      'A Family Tree Maker backup packs your entire tree into a single file that you can tuck away somewhere safe. When you choose to include media, your photos and scanned documents go into that file too, so nothing is left behind.',
      'Hard drives fail, laptops get lost, and files can be damaged without warning. A recent backup turns any of those events from a disaster into a minor inconvenience. If you pick up only one good habit with your genealogy software, make it this one.',
    ],
    sections: [
      {
        heading: 'Making a complete backup',
        steps: [
          'Open the tree you want to protect.',
          'Click the File menu and select Backup.',
          'Pick a destination. An external drive, a flash drive, or a cloud storage folder is better than the same drive the tree lives on.',
          'Tick the option to include media files so pictures and documents are saved along with the tree.',
          'Add the date to the file name, such as your family surname followed by today\'s date, making it easy to spot the most recent one at a glance.',
          'Start the backup and wait for the confirmation message before closing the program.',
        ],
      },
      {
        heading: 'Bringing a backup back',
        steps: [
          'Launch the program, go to the File menu, and pick Restore.',
          'Browse to your backup file and select it.',
          'Give the restored tree a new name, or save it in a different folder, so it does not overwrite a tree you are still using.',
          'Look through the restored tree, confirming the people count and a handful of photos.',
          'If some pictures do not display, run Find Missing Media from the Media workspace.',
        ],
      },
      {
        heading: 'Where to keep your backups',
        bullets: [
          'Follow a simple rule of three: the working tree, one backup on an external drive, and one in the cloud or at a relative\'s house.',
          'Never let your only backup sit on the same computer as the tree.',
          'Replace old flash drives every few years, as they can wear out.',
          'Keep a few older backups as well, in case a problem went unnoticed for a while.',
        ],
      },
      {
        heading: 'A backup schedule you can stick to',
        paragraphs: [
          'The ideal is a quick backup at the end of every research session. If that feels like too much, aim for once a week, and always make an extra one before installing an update, fixing a sync problem, compacting the tree, or moving to another computer.',
        ],
      },
      {
        heading: 'Backup mistakes to steer clear of',
        bullets: [
          'Leaving media out, then discovering the restored tree has no pictures.',
          'Overwriting the only backup with a new one that turns out to be damaged.',
          'Restoring on top of the working tree instead of saving under a new name.',
          'Never testing a backup, so problems only show up when you need it most.',
        ],
      },
      {
        heading: 'Give your backup a test run now and then',
        paragraphs: [
          'Every few months, restore a backup under a new name, open it, and look around. If people, sources, and pictures all appear, you can trust your routine. Delete the test copy afterward so you do not mix it up with your real tree.',
        ],
      },
    ],
    faqs: [
      {
        q: 'If I do not pick a location, where do backups end up?',
        a: 'It usually offers a folder named Family Tree Maker within your Documents folder. Feel free to pick a different place every time, and a cloud folder or external drive is the safer choice.',
      },
      {
        q: 'Is syncing with Ancestry the same as a backup?',
        a: 'Not quite. FamilySync keeps an online tree in step with yours, but not every item or setting is stored the same way online. A regular backup file on your own drive is still essential.',
      },
      {
        q: 'Why is my backup file so big?',
        a: 'Including media adds every photo and document to the file, which is normal. If size is a concern, keep your backups on an external drive with plenty of room.',
      },
      {
        q: 'Can I restore a backup on a different computer?',
        a: 'Yes. Copy the backup file to the new computer, set up the 2024 edition there with your serial number, and then use Restore in the File menu.',
      },
    ],
  },
  {
    slug: 'ftm-2024-not-opening-fix',
    title: 'Family Tree Maker 2024 Refuses to Start or Keeps Closing? Try This',
    excerpt:
      'Calm, practical steps for times FTM 2024 fails to launch, freezes on its opening screen, or shuts down suddenly while you work.',
    intro: [
      'It is unsettling when a program you depend on suddenly will not open, especially one that holds years of family research. Take a breath: in the great majority of cases, your tree file is perfectly safe on your computer.',
      'Start-up trouble and sudden closing usually come from the program needing an update, a clash with security software, a damaged installation, or one particular tree that needs tidying. Working through the steps below, from simplest to most involved, clears up most of these situations.',
    ],
    sections: [
      {
        heading: 'Why this happens',
        bullets: [
          'An operating system update changed something the program relies on, and Family Tree Maker needs its own update to catch up.',
          'Antivirus or other security software is blocking part of the program as it loads.',
          'Program files were damaged during an earlier update or an unexpected power cut.',
          'A single tree has grown very large or developed internal errors that trip the program up.',
        ],
      },
      {
        heading: 'Easy things to try first',
        steps: [
          'Restart the computer fully, rather than just closing the lid or logging out.',
          'If the program opens at all, go to the Help menu, check for updates, and install whatever is offered.',
          'Let Windows Update or macOS Software Update finish anything pending, then restart once more.',
          'Pause your antivirus briefly and see whether the program opens. Turn protection back on afterward.',
        ],
      },
      {
        heading: 'When just one tree causes the trouble',
        paragraphs: [
          'If Family Tree Maker opens but closes as soon as a certain tree loads, the program itself is probably fine. Try opening a different tree, or create a small new tree just for testing. If those behave normally, the problem lies with that one file.',
          'For the troublesome tree, run Compact File from the File menu, which cleans up and reorganizes the database. If that does not help, restore your most recent backup under a new name and carry on from that copy.',
        ],
      },
      {
        heading: 'Reinstalling the program the safe way',
        steps: [
          'Find where your trees and backup files are kept, normally a Family Tree Maker folder within Documents, and put copies on a flash drive or in cloud storage.',
          'Uninstall Family Tree Maker using the normal method for your computer.',
          'Restart the computer.',
          'Grab a new copy of the setup file from your purchase link and run it.',
          'Activate with your serial number, then open your tree.',
        ],
      },
      {
        heading: 'If it still will not cooperate',
        bullets: [
          'Sign in with a different user account on the same computer to see whether the problem follows you.',
          'Check that your hard drive is not nearly full.',
          'Move the tree off any network drive or constantly syncing cloud folder and onto the computer\'s own drive.',
          'Write down the exact wording of any error message, since that makes getting help much quicker.',
        ],
      },
      {
        heading: 'Habits that help prevent future crashes',
        bullets: [
          'Install Family Tree Maker updates when they are offered.',
          'Compact large trees every so often.',
          'Close the program properly before shutting down the computer.',
          'Keep regular backups so a damaged file is never a crisis.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Will uninstalling wipe out my family tree?',
        a: 'No. Your trees are kept separately from the program itself, normally inside your Documents folder. Even so, copy them somewhere safe before uninstalling, just to be careful.',
      },
      {
        q: 'The program freezes on its opening screen. Is my tree ruined?',
        a: 'Not necessarily. A freeze at start-up is more often caused by the program or by security software than by the tree. Work through the steps above before assuming the file is the problem.',
      },
      {
        q: 'What does Compact File actually do?',
        a: 'It reorganizes the database behind your tree, removing wasted space and tidying minor internal issues. It does not delete people or facts, but making a backup first is always wise.',
      },
      {
        q: 'It closes in the middle of a sync. What should I try?',
        a: 'Make sure you have the latest update, restart, and try the sync again. If it keeps happening, make a backup before trying anything else so your copy on the computer stays protected.',
      },
    ],
  },
];
